// Loads data for details page
import {
  CategoryActionTypes,
  CategoryName,
  PendingUser,
  RootActionTypes,
  RootState,
  SharedCategoryState,
  Tab
} from "./userGroupDetailsTypes";
import {
  CategoryDiv,
  CategorySwitcher,
  LoadingIndicator,
  NoResultsIndicatorAnimation
} from "elements";
import React, { ReactNode } from "react";
import { Layout } from "views/Main/Layouts";
import { LocalKeys, useTranslation } from "utilities/i18n";
import {
  Maybe,
  UserGroup,
  UserGroupResponseMessage,
  UserGroupSourceType
} from "generated/graphql.schema";
import { ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { DeviceUsersTable } from "routes/Manage/DeviceUsers/Shared/DeviceUsers/DeviceUsersTable";
import { DeviceUserInfo } from "routes/Manage/DeviceUsers/Shared/utils/deviceUserUtils";
import { useDeviceUserCount } from "routes/Manage/DeviceUsers/Shared/utils/useDeviceUserCount";
import {
  AddUsersModal,
  RemoveUsersModal
} from "routes/Manage/DeviceUsers/Shared/Modals";
import { needs, useGuard } from "rbac";
import {
  getColumns,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit
} from "collections/Table";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { generateTableHeaders } from "utilities/tableUtils";
import { handleNullFields } from "utilities/stringUtils";
import InviteMemberModal from "routes/Admin/Accounts/Members/InviteMemberModal";
import { RelevantTenantInfo } from "routes/Admin/Accounts/Members/Helpers";

export const UserGroupDetailsView: React.FC<{
  rootState: RootState;
  rootStateUpdate: RootActionTypes;
  groupedState: SharedCategoryState;
  groupedUpdate: CategoryActionTypes;
  availableState: SharedCategoryState;
  availableUpdate: CategoryActionTypes;
  availablePageInfo?: {
    hasNextPage: boolean;
    endCursor: string;
  };
  groupData:
    | Omit<UserGroup, "responseMessage" | "genericUsers" | "users" | "policies">
    | undefined;
  responseMessage?: UserGroupResponseMessage;
  availableUsers: Array<DeviceUserInfo>;
  groupedUsers: Array<DeviceUserInfo>;
  pendingUsers: {
    loading: boolean;
    users: Array<PendingUser>;
  };
  tenant: RelevantTenantInfo;
}> = ({
  rootState,
  rootStateUpdate,
  availableState,
  availableUpdate,
  availablePageInfo,
  groupedState,
  groupedUpdate,
  groupData,
  availableUsers,
  groupedUsers,
  pendingUsers,
  responseMessage,
  tenant
}) => {
  const { tab } = rootState;
  const { t } = useTranslation();
  const isManualGroup = groupData?.source === UserGroupSourceType.Lens;
  const isLackingPermissions =
    responseMessage === UserGroupResponseMessage.NoMembershipPermissions;
  const tenantId = tenant.id;

  const { deviceUserCount } = useDeviceUserCount(tenantId);
  const userCanEdit = useGuard(needs("update:usergroup")) && isManualGroup;

  const switchTab = (tab: Tab) => {
    rootStateUpdate.updateTab(tab);
  };

  const inThisGroupCount = groupedState.pageLoading
    ? "?"
    : `${groupedUsers.length}`;
  const availableToAddCount =
    groupedState.pageLoading ||
    availableState.pageLoading ||
    deviceUserCount == null
      ? "?"
      : `${deviceUserCount - groupedUsers?.length}`;
  const pendingCount = pendingUsers.users.length;

  let groupedUsersEmptyStateMessage: LocalKeys | undefined =
    "NO_USERS_IN_USER_GROUP_MESSAGE";
  if (!isManualGroup) {
    groupedUsersEmptyStateMessage = undefined;
    if (isLackingPermissions) {
      groupedUsersEmptyStateMessage = "NO_MEMBERSHIP_PERMISSIONS_INFO";
    }
  }

  const { Content } = Layout;
  return (
    <Content className="device-group-details device-inventory">
      <DeviceUserGroupTitle groupName={groupData?.name ?? ""} page="OVERVIEW" />
      <section>
        <CategorySwitcher>
          <CategoryDiv
            label={t("IN_THIS_GROUP", { inThisGroupCount })}
            active={tab === Tab.GROUPED_USERS}
            onClick={() => switchTab(Tab.GROUPED_USERS)}
          />
          {isManualGroup && (
            <CategoryDiv
              label={t("AVAILABLE_TO_ADD", { availableToAddCount })}
              active={tab === Tab.AVAILABLE_TO_ADD}
              onClick={() => switchTab(Tab.AVAILABLE_TO_ADD)}
            />
          )}
          {!isManualGroup && (
            <CategoryDiv
              label={t("PENDING", { count: pendingCount })}
              active={tab === Tab.PENDING_USERS}
              onClick={() => switchTab(Tab.PENDING_USERS)}
            />
          )}
        </CategorySwitcher>
      </section>
      {tab === Tab.GROUPED_USERS && (
        <DeviceUsersTable
          actions={
            isManualGroup
              ? [
                  props => (
                    <ProtectedLink
                      nest
                      to={{ pathname: "/remove" }}
                      className={[
                        "btn",
                        "btn-primary",
                        props.disabled ? "btn-disabled" : undefined
                      ]
                        .filter(c => !!c)
                        .join(" ")}
                      requirement="update:usergroup"
                      disabled={props.disabled}
                    >
                      {t("REMOVE_FROM_GROUP")}
                    </ProtectedLink>
                  )
                ]
              : []
          }
          routes={
            groupData && isManualGroup
              ? [
                  {
                    nestedRoute: "/remove",
                    requirement: "grant:role",
                    refresh: () => {},
                    Modal: ({ deviceUsers }) => (
                      <RemoveUsersModal
                        tenantId={tenantId}
                        groupData={groupData}
                        showLinkOnSuccess={false}
                        deviceUsers={deviceUsers}
                        urlEndPath="/remove"
                      />
                    )
                  }
                ]
              : []
          }
          loading={groupedState.pageLoading}
          userCanEdit={userCanEdit}
          deviceUserList={groupedUsers ?? []}
          selectedItems={groupedState.selectedItems}
          setSelectedItems={(items: string[]) => {
            groupedUpdate.updateSelectedItems(
              CategoryName.GROUPED_USERS,
              items
            );
          }}
          hasDeviceUsers={(groupedUsers ?? []).length > 0}
          noUsersMessage={groupedUsersEmptyStateMessage}
        />
      )}
      {tab === Tab.AVAILABLE_TO_ADD && (
        <DeviceUsersTable
          actions={[
            props => (
              <ProtectedLink
                nest
                to={{ pathname: "/add" }}
                className={[
                  "btn",
                  "btn-primary",
                  props.disabled ? "btn-disabled" : undefined
                ]
                  .filter(c => !!c)
                  .join(" ")}
                requirement="update:usergroup"
                disabled={props.disabled}
              >
                {t("ADD_TO_GROUP")}
              </ProtectedLink>
            )
          ]}
          routes={
            groupData
              ? [
                  {
                    nestedRoute: "/add",
                    requirement: "grant:role",
                    refresh: () => {},
                    Modal: ({ deviceUsers }) => (
                      <AddUsersModal
                        tenantId={tenantId}
                        groupData={groupData}
                        showLinkOnSuccess={false}
                        deviceUsers={deviceUsers}
                        urlEndPath="/add"
                      />
                    )
                  }
                ]
              : []
          }
          loading={availableState.pageLoading}
          userCanEdit={userCanEdit}
          setUsersSearchTerm={(term: string | undefined) => {
            availableUpdate.updateSearchTerm(
              CategoryName.AVAILABLE_TO_ADD,
              term
            );
          }}
          hasNextPage={availablePageInfo?.hasNextPage}
          endCursor={availablePageInfo?.endCursor}
          deviceUserList={availableUsers}
          selectedItems={availableState.selectedItems}
          setSelectedItems={(items: string[]) => {
            availableUpdate.updateSelectedItems(
              CategoryName.AVAILABLE_TO_ADD,
              items
            );
          }}
          hasDeviceUsers={
            availableUsers.filter(
              user => !groupedUsers.find(gu => gu.user_id === user.user_id)
            ).length > 0 ||
            (availableState.searchTerm != null &&
              availableState.searchTerm.length !== 0)
          }
          hiddenItems={groupedUsers.map(user => user.user_id)}
          updateCursor={(cursor: string) => {
            availableUpdate.updateCursor(CategoryName.AVAILABLE_TO_ADD, cursor);
          }}
          noUsersMessage={"NO_USERS_AVAILABLE_TO_ADD_MESSAGE"}
        />
      )}
      {tab === Tab.PENDING_USERS && (
        <PendingUsersTable
          pendingUsers={pendingUsers.users}
          loading={pendingUsers.loading}
          tenant={tenant}
          isLackingPermissions={isLackingPermissions}
        />
      )}
    </Content>
  );
};

const DeviceUserGroupTitle: React.FC<{
  groupName: string;
  page: LocalKeys;
}> = ({ groupName, page }) => {
  const { t } = useTranslation();
  const { Content } = Layout;
  return (
    <Content.Title>
      {`${t(page)} `}
      <p>{`| ${groupName}`}</p>
    </Content.Title>
  );
};

const PendingUsersTable: React.FC<{
  pendingUsers: Array<PendingUser>;
  loading: boolean;
  tenant: RelevantTenantInfo;
  isLackingPermissions?: boolean;
}> = ({ pendingUsers, loading, tenant, isLackingPermissions }) => {
  const { t } = useTranslation();

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.PENDING_USERS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const columnTranslations = {
    [COLUMNS.NAME]: t("DEVICE_USER_NAME"),
    [COLUMNS.EMAIL]: t("EMAIL")
  };

  const columnOrder = [COLUMNS.NAME, COLUMNS.EMAIL];

  const getTableHeaders = (column: string, title: string) => {
    return <TableHeaderItem fieldName={column} title={title} key={column} />;
  };
  const shown = getColumns(shownColumns, columnTranslations);
  shown.sort((a, b) => columnOrder.indexOf(a.key) - columnOrder.indexOf(b.key));
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const columnCount = shown.length;
  const hasDeviceUsers = pendingUsers.length > 0;

  if (!loading && !hasDeviceUsers) {
    return (
      <NoResultsIndicatorAnimation
        className="inline pt-4"
        title={t("NO_DEVICE_USERS")}
      >
        {isLackingPermissions && (
          <span className="font-sm">{t("NO_MEMBERSHIP_PERMISSIONS_INFO")}</span>
        )}
      </NoResultsIndicatorAnimation>
    );
  }

  return (
    <>
      <div className="grouping mb-15 table-actions">
        <ProtectedLink
          nest
          to={{ pathname: "/invite", search: "device-user=true" }}
          className="btn btn-primary"
          requirement="grant:role"
        >
          {t("INVITE_PENDING_DEVICE_USERS")}
        </ProtectedLink>
        <TableSearchEdit
          edit={{
            tableName: TABLE_NAME,
            shownColumns: shown,
            hiddenColumns: hidden
          }}
        />
      </div>
      <div className="table-container">
        <table className="table custom-table table-filterable">
          <TableBulkItemHeader
            showChecks={false}
            checked={false}
            onCheck={() => {}}
            onClearFilters={() => {}}
            showClearFilters={false}
          >
            {generateTableHeaders(shown, getTableHeaders)}
          </TableBulkItemHeader>
          <TableBody loading={false} cols={columnCount} accommodateScrollbar>
            {pendingUsers.map((user, i) => {
              return (
                <PendingUserRow
                  {...user}
                  key={`${i}-${user.email}`}
                  shownColumns={shown.map(column => column.key)}
                />
              );
            })}
          </TableBody>
        </table>
        {loading ? (
          <LoadingIndicator
            key="loading"
            className={pendingUsers.length ? "inline text-lg" : ""}
          />
        ) : null}
      </div>
      <ProtectedRoute nest path="/invite" requirement="grant:role">
        <InviteMemberModal
          userEmails={pendingUsers
            ?.filter(({ email }) => email)
            .map(({ email }) => email as string)}
          tenant={tenant}
        />
      </ProtectedRoute>
    </>
  );
};

export type PendingUserRowProps = {
  name: Maybe<string>;
  email: Maybe<string>;
  shownColumns: Array<string>;
};

export const PendingUserRow: React.FC<PendingUserRowProps> = ({
  name,
  email,
  shownColumns
}) => {
  const { COLUMNS } = TABLES.PENDING_USERS;
  const getTableContent = (column: string) => {
    let content: ReactNode = null;

    switch (column) {
      case COLUMNS.NAME:
        content = <span>{handleNullFields(name)}</span>;
        break;
      case COLUMNS.EMAIL:
        content = <span>{handleNullFields(email)}</span>;
        break;
    }
    return <td key={column}>{content}</td>;
  };
  return (
    <TableBulkItemRow checked={false} onCheck={() => {}} showChecks={false}>
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};
